import { useState } from "react";
import { Button, HStack } from "@chakra-ui/react";
import Plot from "react-plotly.js";
import { Historial } from "../utils/interfaces";
import {
  getIssuePlotColor,
  getLightTextIssueColor,
} from "../utils/errorColors";
import parseTime from "../utils/parseTime";

const HOUR = 1000 * 60 * 60;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 28 * DAY; // Febrero tiene 28 días. Es mejor usar 28 para evitar overflow en el gráfico.
const SEASON = 90 * DAY;

function getSeasonColor(date: Date): string {
  const mi = date.getMonth();
  if (mi < 3) {
    return "hsl(170, 70%, 40%)";
  }
  if (mi < 6) {
    return "hsl(10, 30%, 45%)";
  }
  if (mi < 9) {
    return "hsl(220, 30%, 50%)";
  }
  return "hsl(330, 60%, 60%)";
}

const GroupBy = {
  none: "No agrupar",
  hour: "Hora",
  day: "Día",
  week: "Semana",
  month: "Mes",
  season: "Trimestre",
};

const PLOT_OR_TABLE_WIDTH_INT = 450;
const PLOT_OR_TABLE_WIDTH_STR = `${PLOT_OR_TABLE_WIDTH_INT}px`;

/**
 * Un gráfico para un historial de datos. Se usa Plotly.js para dibujarlo.
 */
export default function FPPlot({
  historial,
}: {
  historial: Historial | null;
}): JSX.Element {
  const [groupBy, setGroupBy] = useState(GroupBy.none);

  if (historial === null) {
    return (
      <p style={{ maxWidth: PLOT_OR_TABLE_WIDTH_STR, textAlign: "center" }}>
        Haz click en una variable para mostrar su evolución en el tiempo.
      </p>
    );
  }

  const { tipo_evento, mapa_valores, tiempos, gravedades } = historial;
  const { nombre_variable, descripcion, tipo_dato, unidad_medida, es_agrupable } = tipo_evento;
  const { max, min, main, desvest } = mapa_valores;
  if (main.length === 0) {
    return (
      <p style={{ maxWidth: PLOT_OR_TABLE_WIDTH_STR, textAlign: "center" }}>
        Sin datos para "{descripcion ?? "Variable desconocida"}"
      </p>
    );
  }

  const estaAgrupado = es_agrupable && groupBy !== GroupBy.none;

  // Se muestra una tabla si el valor es de tipo texto. No tiene mucho
  // sentido mostrar un gráfico de tiempo
  if (tipo_dato === "string") {
    return (
      <table
        style={{
          display: "block",
          overflowY: "auto",
          maxHeight: "460px",
          maxWidth: PLOT_OR_TABLE_WIDTH_STR,
          margin: "auto",
        }}
      >
        <thead style={{ position: "sticky", top: "0" }}>
          <tr style={{ backgroundColor: "hsl(0, 0%, 80%)" }}>
            <th style={{ paddingInline: "1em" }}>Tiempo</th>
            <th style={{ position: "sticky", paddingInline: "1em" }}>
              {descripcion ?? "Variable desconocida"}
            </th>
          </tr>
        </thead>
        <tbody>
          {main.map((valor, i) => (
            <tr
              style={{ backgroundColor: getLightTextIssueColor(gravedades[i]) }}
            >
              <td
                style={{
                  width: "11.5rem",
                  borderWidth: "1px",
                  borderColor: "hsl(0, 0%, 90%)",
                  paddingInline: "1em",
                }}
              >
                {parseTime(tiempos[i])}
              </td>
              <td
                style={{
                  minWidth: "0px",
                  borderWidth: "1px",
                  borderColor: "hsl(0, 0%, 90%)",
                  paddingInline: "1em",
                }}
              >
                {nombre_variable === "ip_actual"
                ? <a
                    href={`https://whatismyipaddress.com/ip/${valor}`}
                    style={{ color: "blue", textDecorationLine: "underline" }}
                  >
                    {valor}
                  </a>
                : valor}
              </td>
            </tr>
          )).reverse() /* SE INVIERTE EL ORDEN */}
        </tbody>
      </table>
    );
  }

  const colores = [];
  for (const gravedad of gravedades) {
    colores.push(getIssuePlotColor(gravedad));
  }

  // Agrupación de datos
  const tiemposAgrupados: string[] = [];
  const valoresAgrupados: number[] = [];
  const coloresAgrupados: string[] = [];

  if (estaAgrupado) {
    let endDate = new Date(0);
    let groupIndex = -1;
    for (let i = 0; i < main.length; i++) {
      if (tiempos[i] <= endDate.toISOString()) {
        valoresAgrupados[groupIndex] += main[i];
        continue;
      }
      groupIndex++;
      let startDate = new Date(tiempos[i]);
      if (groupBy === GroupBy.hour) {
        startDate.setMinutes(0, 0, 0);
      } else {
        startDate.setHours(0, 0, 0, 0);
      }
      switch (groupBy) {
        case GroupBy.hour:
          endDate = new Date(startDate);
          endDate.setHours(endDate.getHours() + 1);
          break;
        case GroupBy.day:
          endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + 1);
          break;
        case GroupBy.week:
          startDate.setDate(
            startDate.getDate() - ((startDate.getDay() + 6) % 7),
          );
          endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + 7);
          break;
        case GroupBy.month:
          startDate.setDate(1);
          endDate = new Date(startDate);
          endDate.setMonth(endDate.getMonth() + 1);
          break;
        case GroupBy.season:
          const mi = startDate.getMonth();
          startDate.setDate(1);
          startDate.setMonth(mi - (mi % 3));
          endDate = new Date(startDate);
          endDate.setMonth(mi + 3);
          break;
      }

      valoresAgrupados.push(main[i]);
      coloresAgrupados.push(getSeasonColor(startDate));

      // Corrección final para mostrar la fecha sin desfase horario en las barras
      if (groupBy !== GroupBy.hour) {
        startDate.setHours(-startDate.getTimezoneOffset() / 60, 0, 0, 0);
        endDate.setHours(-endDate.getTimezoneOffset() / 60, 0, 0, 0);
      }

      tiemposAgrupados.push(startDate.toISOString());
    }
  }

  // Calcular el máximo y mínimo valor que se mostrará en el gráfico.
  let yValues;
  let yMin;
  let yMax;

  if (tipo_dato === "bool") {
    yValues = main.map((val) => (val ? 1 : 0));
    yMin = -0.1;
    yMax = 1.1;
  } else {
    yValues = estaAgrupado ? valoresAgrupados : main;
    yMin = Math.min(0, ...(min === undefined ? yValues : min));
    yMax = Math.max(0, ...(max === undefined ? yValues : max));
    if (descripcion?.includes("Temperatura")) {
      yMax = Math.min(60.0, yMax);
    }
    if (descripcion?.includes("Humedad")) {
      yMax = Math.min(100.0, yMax);
    }

    const margin = Math.max(0.1, (yMax - yMin) * 0.1);
    yMin -= margin;
    yMax += margin;
  }

  const esMedicion = ["Temperatura", "Humedad", "Voltaje"].some((word) =>
    descripcion?.startsWith(word),
  );

  let barWidth = 1;
  if (estaAgrupado) {
    switch (groupBy) {
      case GroupBy.hour:
        barWidth = HOUR;
        break;
      case GroupBy.day:
        barWidth = DAY;
        break;
      case GroupBy.week:
        barWidth = WEEK;
        break;
      case GroupBy.month:
        barWidth = MONTH;
        break;
      case GroupBy.season:
        barWidth = SEASON;
        break;
    }
    barWidth *= 0.95;
  }

  // Para datos booleanos (sí/no), es mejor agregar una línea con área para indicar mejor los valores en cada momento.
  const tBooleano = [];
  const yBooleano = [];
  if (tipo_dato === "bool") {
    let tAnterior = null
    let yAnterior = null;
    for (let i = 0; i <= tiempos.length; i++) {
      const t = tiempos[i];
      const y = yValues[i];
      if (i === 0 || y === yAnterior) {
        tBooleano.push(t);
        yBooleano.push(y);
      } else {
        tBooleano.push(tAnterior, t);
        yBooleano.push(y, y);
      }
      tAnterior = t;
      yAnterior = y;
    }

  }

  return (
    <div style={{ width: PLOT_OR_TABLE_WIDTH_STR, margin: "auto" }}>
      <Plot
        data={[
          // Para datos booleanos (sí/no), es mejor agregar una línea con área para indicar mejor los valores en cada momento.
          tipo_dato !== "bool"
            ? {}
            : {
              name: "",
              type: "scatter",
              mode: "lines",
              x: tBooleano,
              y: yBooleano,
              fill: "tozeroy",
            },
          {
            name: desvest === undefined ? "" : "Media ± std",
            type: estaAgrupado ? "bar" : "scatter",
            mode: esMedicion ? "lines" : "markers",
            x: estaAgrupado ? tiemposAgrupados : tiempos,
            y: yValues.map((val) => Number(val.toFixed(1))),
            width: estaAgrupado ? barWidth : undefined,
            // Muestra desviación estándar para firmeza, calibre y peso.
            error_y:
              desvest === undefined
                ? undefined
                : {
                    type: "data",
                    array: desvest.map((val) => Number(val.toFixed(1))),
                    visible: true,
                  },
            marker: estaAgrupado
              ? {
                  color: coloresAgrupados,
                }
              : {
                  color: colores,
                  size: 12,
                  symbol: "circle-open",
                  line: {
                    width: 4,
                  },
                },
          },
          // Muestra máximo y mínimo para firmeza, calibre y peso.
          max === undefined
            ? {}
            : {
                name: "Máximo",
                type: "scatter",
                mode: "markers",
                x: tiempos,
                y: max.map((val) => Number(val.toFixed(1))),
                marker: {
                  color: colores,
                  size: 6,
                  symbol: "circle-open",
                  line: {
                    width: 2,
                  },
                },
              },
          min === undefined
            ? {}
            : {
                name: "Mínimo",
                type: "scatter",
                mode: "markers",
                x: tiempos,
                y: min.map((val) => Number(val.toFixed(1))),
                marker: {
                  color: colores,
                  size: 6,
                  symbol: "circle-open",
                  line: {
                    width: 2,
                  },
                },
              },
        ]}
        layout={{
          title: descripcion ?? "Variable desconocida",
          height: 420,
          width: PLOT_OR_TABLE_WIDTH_INT,
          showlegend: false,
          uirevision: "true", // IMPORTANTE para que no se resetee el zoom y pan actual al actualizar historial
          hovermode: "x", // Para que se muestre el tooltip al asomar el cursor en una posición x.
          dragmode: "pan",
          yaxis: {
            title: unidad_medida === null ? "" : unidad_medida,
            ticklabelstep: 1,
            fixedrange: true,
            range: [yMin, yMax],
            tickvals: tipo_dato === "bool" ? [0, 1] : undefined,
            ticktext: tipo_dato === "bool" ? ["No", "Sí"] : undefined,
          },
          margin: {
            l: 30,
            r: 30,
            t: 50,
            b: 70,
          },
        }}
        config={{
          responsive: true,
          scrollZoom: true,
          displayModeBar: false,
        }}
      />
      <HStack gap="0" m="0" height="2em" justifyContent="space-around">
        {!es_agrupable ? (
          <p style={{ color: "hsl(0, 0%, 60%)" }}>
            Este valor no es agrupable.
          </p>
        ) : (
          Object.values(GroupBy).map((value) => (
            <Button
              key={value}
              colorScheme="teal"
              borderRadius="0"
              variant={value === groupBy ? "solid" : "outline"}
              onClick={() => setGroupBy(value)}
            >
              {value}
            </Button>
          ))
        )}
      </HStack>
    </div>
  );
}
